import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useBranchStore } from '../model/branch.store';
import type { Branch, BranchFormData } from '@/entities/Branch';
import { buildFormData, toastError, toastSuccess, useCustomToast } from '@/shared/lib';
import { useBranchLogo } from '@/entities/Branch';

/**
 * Composable for branch management operations
 * Provides high-level methods for creating, updating, and deleting branches
 */
export function useBranchManagement() {
  const { getBranchLogoUrl } = useBranchLogo();

  const store = useBranchStore();
  const router = useRouter();
  const toast = useCustomToast();
  // ===== COMPUTED =====
  const branches = computed(() => store.branches);
  const currentBranch = computed(() => store.currentBranch);
  const isLoading = computed(() => store.isLoading);
  const isCreating = computed(() => store.isCreating);
  const isUpdating = computed(() => store.isUpdating);
  const isDeleting = computed(() => store.isDeleting);
  const error = computed(() => store.error);
  const hasBranches = computed(() => store.hasBranches);
  const matrizBranch = computed(() => store.matrizBranch);

  const sanitizeOptionalField = (value?: string) => {
    return value === '' ? undefined : value;
  };

  // ===== METHODS =====
  async function loadBranch(branchId: number | string) {
    const branch = await store.fetchBranchById(branchId);

    if (!branch) return null;

    if (branch.logoMinioObjectName) {
      branch.logoPath = getBranchLogoUrl(branch as Branch);
    }
    return branch;
  }

  async function createBranch(
    formData: BranchFormData,
    logo: File | null
  ): Promise<{ success: boolean }> {
    const data = buildFormData({
      name: formData.name,
      colorPrimary: formData.colorPrimary,
      colorSecondary: formData.colorSecondary,
      colorText: formData.colorText,
      colorPrimaryDark: formData.colorPrimaryDark,
      colorSecondaryDark: formData.colorSecondaryDark,
      colorTextDark: formData.colorTextDark,
      file: logo ?? undefined,
    });

    const success = await store.createBranch(data);

    if (success) {
      toastSuccess(toast, 'Filial criada com sucesso');
      router.push('/admin/filiais');
    } else {
      toastError(toast, 'Erro ao criar Filial');
    }

    return { success };
  }

  async function updateBranch(
    branchId: number | string,
    formData: Partial<BranchFormData>,
    logo?: File | null
  ): Promise<{ success: boolean }> {
    const data = buildFormData({
      name: sanitizeOptionalField(formData.name),
      colorPrimary: sanitizeOptionalField(formData.colorPrimary),
      colorSecondary: sanitizeOptionalField(formData.colorSecondary),
      colorText: sanitizeOptionalField(formData.colorText),
      colorPrimaryDark: sanitizeOptionalField(formData.colorPrimaryDark),
      colorSecondaryDark: sanitizeOptionalField(formData.colorSecondaryDark),
      colorTextDark: sanitizeOptionalField(formData.colorTextDark),
      file: logo ?? undefined,
    });

    const success = await store.updateBranch(branchId, data);

    if (success) {
      toastSuccess(toast, 'Edição salva com sucesso!');
      router.push('/admin/filiais');
    } else {
      toastError(toast, 'Erro ao salvar edição. Por favor, tente novamente.');
    }

    return { success };
  }

  async function deleteBranch(branchId: number): Promise<{ success: boolean }> {
    // Check if it's Matriz branch
    const branchToDelete = branches.value.find((b) => b.id === branchId);
    if (branchToDelete?.isMain || branchToDelete?.name === 'Matriz') {
      toastError(toast, 'Não é possível deletar a filial Matriz');
      return { success: false };
    }

    const success = await store.deleteBranch(branchId);

    if (success) {
      toastSuccess(toast, 'Filial deletada com sucesso');
    } else {
      toastError(toast, 'Erro ao deletar Filial. Por favor, tente novamente.');
    }

    return { success };
  }

  function canDeleteBranch(branch: Branch): boolean {
    return !(branch.isMain || branch.name === 'Matriz');
  }

  function navigateToCreate() {
    router.push('/admin/filiais/criar-filial');
  }

  function navigateToEdit(branchId: number) {
    router.push(`/admin/filiais/${branchId}`);
  }

  function navigateToList() {
    router.push('/admin/filiais');
  }

  function clearError() {
    store.clearError();
  }

  function reset() {
    store.reset();
  }

  // ===== RETURN =====
  return {
    // State
    branches,
    currentBranch,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    error,
    hasBranches,
    matrizBranch,
    // Methods
    loadBranch,
    createBranch,
    updateBranch,
    deleteBranch,
    canDeleteBranch,
    navigateToCreate,
    navigateToEdit,
    navigateToList,
    clearError,
    reset,
  };
}
